import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Camera, Instagram, Youtube, Facebook, Music2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { updateTrainerFullProfile } from '../lib/api';
import { getInitials } from '../lib/utils';
import ImageCropper from './ImageCropper';
import WeeklyScheduleEditor from './WeeklyScheduleEditor';
import { Switch } from './ui/switch';

const SPECIALTY_OPTIONS = ['BJJ', 'Self Defense', 'Personal Training', 'Fitness', 'Other'];

/**
 * Compress an image file to a JPEG data URI by drawing it to a canvas with
 * `maxEdge` as the longest-side cap. Keeps aspect ratio. Auto-rotates on
 * decode by the browser. Any input (HEIC, PNG, HD JPEG) becomes ~200-500KB.
 */
const compressImageFile = (file, maxEdge = 1600, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the image'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not decode the image'));
      img.onload = () => {
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const EditTrainerProfileDialog = ({ open, onOpenChange }) => {
  const { user, refreshUser } = useAuth();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [form, setForm] = useState(null);

  // Initialize/reset form whenever modal opens or user changes
  useEffect(() => {
    if (!open || !user) return;
    const p = user.trainer_profile || {};
    setForm({
      full_name: user.full_name || '',
      phone: p.phone || user.phone || '',
      contact_email: p.contact_email || user.email || '',
      bio: p.bio || '',
      specializations: Array.isArray(p.specializations) ? [...p.specializations] : [],
      price_personal_training: p.price_personal_training ?? (p.hourly_rate || ''),
      price_self_defense: p.price_self_defense ?? (p.hourly_rate || ''),
      price_jiu_jitsu: p.price_jiu_jitsu ?? (p.hourly_rate || ''),
      service_area: p.service_area || user.city || '',
      state: p.state || '',
      profile_image: p.profile_image || '',
      social_instagram: p.social_instagram || '',
      social_tiktok: p.social_tiktok || '',
      social_youtube: p.social_youtube || '',
      social_facebook: p.social_facebook || '',
      weekly_schedule: p.weekly_schedule || {},
      accepts_at_my_location: !!p.accepts_at_my_location,
      trainer_address: p.trainer_address || '',
      certifications_text: Array.isArray(p.certifications) ? p.certifications.join('\n') : '',
      experience_years: p.experience_years == null ? '' : String(p.experience_years),
      equipment: p.equipment || 'none',
      equipment_details: p.equipment_details || '',
      experience_levels: Array.isArray(p.experience_levels) ? [...p.experience_levels] : [],
      portfolio_photos: Array.isArray(p.portfolio_photos) ? [...p.portfolio_photos].slice(0, 4) : [],
      portfolio_video_url: p.portfolio_video_url || '',
    });
  }, [open, user]);

  if (!form) return null;

  const setField = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const toggleSpecialty = (s) => {
    setForm((f) => {
      const exists = f.specializations.includes(s);
      return {
        ...f,
        specializations: exists ? f.specializations.filter((x) => x !== s) : [...f.specializations, s],
      };
    });
  };

  const handleFileChange = (e) => {
    // legacy path kept for backwards compat (no longer wired)
  };

  const removeImage = () => {
    setField('profile_image', '');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }
    const parsePrice = (val, label) => {
      if (val === '' || val === null || val === undefined) return null;
      const n = Number(val);
      if (Number.isNaN(n) || n < 0) {
        toast.error(`${label} must be a positive number`);
        throw new Error('invalid');
      }
      if (n < 40) {
        toast.error('Minimum session rate is $40.');
        throw new Error('invalid');
      }
      return Math.round(n);
    };
    let priceTraining, priceSelfDefense, priceJiuJitsu;
    try {
      priceTraining = parsePrice(form.price_personal_training, 'Personal Training price');
      priceSelfDefense = parsePrice(form.price_self_defense, 'Self-Defense price');
      priceJiuJitsu = parsePrice(form.price_jiu_jitsu, 'Jiu-Jitsu price');
    } catch {
      return;
    }

    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone?.trim() || null,
        city: form.service_area?.trim() || null,
        trainer_profile: {
          bio: form.bio || null,
          specializations: form.specializations,
          price_personal_training: priceTraining,
          price_self_defense: priceSelfDefense,
          price_jiu_jitsu: priceJiuJitsu,
          service_area: form.service_area?.trim() || null,
          state: form.state?.trim() || null,
          profile_image: form.profile_image || null,
          phone: form.phone?.trim() || null,
          contact_email: form.contact_email?.trim() || null,
          social_instagram: form.social_instagram?.trim() || null,
          social_tiktok: form.social_tiktok?.trim() || null,
          social_youtube: form.social_youtube?.trim() || null,
          social_facebook: form.social_facebook?.trim() || null,
          weekly_schedule: form.weekly_schedule || {},
          accepts_at_my_location: !!form.accepts_at_my_location,
          trainer_address: form.trainer_address?.trim() || null,
          certifications: (form.certifications_text || '')
            .split('\n')
            .map((s) => s.trim())
            .filter(Boolean),
          experience_years:
            form.experience_years === '' || form.experience_years == null
              ? 0
              : Math.max(0, Math.min(70, Number(form.experience_years) || 0)),
          equipment: form.equipment || 'none',
          equipment_details: form.equipment_details?.trim() || null,
          experience_levels: form.experience_levels || [],
          portfolio_photos: (form.portfolio_photos || []).filter(Boolean).slice(0, 4),
          portfolio_video_url: form.portfolio_video_url?.trim() || null,
        },
      };
      await updateTrainerFullProfile(payload);
      await refreshUser();
      toast.success('Profile updated successfully');
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.detail || 'Failed to update profile';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#18181B] border-zinc-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="edit-trainer-profile-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">Edit profile</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Update your public trainer profile. Changes are visible to clients immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Profile picture */}
          <div className="flex items-center gap-4">
            <Avatar className="w-20 h-20 border-2 border-[#C0A062]/40">
              <AvatarImage src={form.profile_image} alt={form.full_name} />
              <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-xl font-bold">
                {getInitials(form.full_name || 'T')}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                  onClick={() => setCropperOpen(true)}
                  data-testid="upload-image-btn"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {form.profile_image ? 'Change photo' : 'Upload photo'}
                </Button>
                {form.profile_image && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                    onClick={removeImage}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-zinc-500">Any size accepted, we&apos;ll auto-crop &amp; compress.</p>
            </div>
          </div>

          {/* Full name */}
          <div className="grid gap-2">
            <Label htmlFor="full_name" className="text-zinc-300">Full name</Label>
            <Input
              id="full_name"
              value={form.full_name}
              onChange={(e) => setField('full_name', e.target.value)}
              className="bg-[#09090B] border-zinc-800 text-white"
              data-testid="input-full-name"
            />
          </div>

          {/* Bio */}
          <div className="grid gap-2">
            <Label htmlFor="bio" className="text-zinc-300">Bio / About me</Label>
            <Textarea
              id="bio"
              rows={4}
              value={form.bio}
              onChange={(e) => setField('bio', e.target.value)}
              placeholder="Tell clients about your experience, training style, and what makes you stand out."
              className="bg-[#09090B] border-zinc-800 text-white"
              data-testid="input-bio"
            />
          </div>

          {/* Specialties */}
          <div className="grid gap-2">
            <Label className="text-zinc-300">Specialties</Label>
            <div className="flex flex-wrap gap-2" data-testid="specialties-list">
              {SPECIALTY_OPTIONS.map((opt) => {
                const active = form.specializations.includes(opt);
                return (
                  <button
                    type="button"
                    key={opt}
                    onClick={() => toggleSpecialty(opt)}
                    data-testid={`specialty-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-[#C0A062] border-[#C0A062] text-black font-medium'
                        : 'bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Certifications */}
          <div className="grid gap-2">
            <Label htmlFor="certifications" className="text-zinc-300">Certifications (one per line)</Label>
            <Textarea
              id="certifications"
              rows={4}
              value={form.certifications_text}
              onChange={(e) => setField('certifications_text', e.target.value)}
              placeholder={'NASM Certified Personal Trainer\nBJJ Brown Belt, Gracie Barra\nCPR / AED Certified'}
              className="bg-[#09090B] border-zinc-800 text-white"
              data-testid="input-certifications"
            />
            <p className="text-xs text-zinc-500">
              All trainers self-report their credentials. DojoDash does not independently verify
              certifications or conduct background checks.
            </p>
          </div>

          {/* Experience */}
          <div className="grid gap-2">
            <Label htmlFor="experience_years" className="text-zinc-300">
              Years of experience
            </Label>
            <Input
              id="experience_years"
              type="number"
              min={0}
              max={70}
              step={1}
              value={form.experience_years}
              onChange={(e) => setField('experience_years', e.target.value)}
              placeholder="e.g. 5"
              className="bg-[#09090B] border-zinc-800 text-white max-w-[160px]"
              data-testid="input-experience-years"
            />
            <p className="text-xs text-zinc-500">
              Enter 0 if you have less than a year of professional coaching experience.
            </p>
          </div>

          {/* Equipment */}
          <div className="grid gap-3 border border-zinc-800 rounded-md p-4 bg-[#0d0d10]">
            <Label className="text-zinc-200 font-semibold">Equipment</Label>
            <div className="flex flex-wrap gap-2" data-testid="equipment-options">
              {[
                { id: 'full', label: 'I bring full equipment' },
                { id: 'some', label: 'I bring some equipment' },
                { id: 'none', label: 'No equipment, client provides' },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.id}
                  onClick={() => setField('equipment', opt.id)}
                  data-testid={`equipment-${opt.id}`}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    form.equipment === opt.id
                      ? 'bg-[#C0A062] border-[#C0A062] text-black font-medium'
                      : 'bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {(form.equipment === 'full' || form.equipment === 'some') && (
              <Input
                value={form.equipment_details}
                onChange={(e) => setField('equipment_details', e.target.value)}
                placeholder="Describe what you bring (gloves, mitts, resistance bands, mats, etc.)"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="input-equipment-details"
              />
            )}
          </div>

          {/* Experience levels trained */}
          <div className="grid gap-2">
            <Label className="text-zinc-300">Experience levels you train</Label>
            <div className="flex flex-wrap gap-2" data-testid="experience-levels-options">
              {[
                { id: 'beginner', label: 'Beginner' },
                { id: 'intermediate', label: 'Intermediate' },
                { id: 'advanced', label: 'Advanced' },
                { id: 'competition', label: 'Competition-level' },
              ].map((opt) => {
                const active = (form.experience_levels || []).includes(opt.id);
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => setField('experience_levels',
                      active
                        ? form.experience_levels.filter((x) => x !== opt.id)
                        : [...(form.experience_levels || []), opt.id]
                    )}
                    data-testid={`exp-level-${opt.id}`}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-[#C0A062] border-[#C0A062] text-black font-medium'
                        : 'bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Pricing, per-service rates */}
          <div className="grid gap-3">
            <Label className="text-zinc-300">Pricing per service ($/session)</Label>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { key: 'price_personal_training', label: 'Personal Training', testid: 'input-price-personal-training', placeholder: '75' },
                { key: 'price_self_defense', label: 'Self-Defense', testid: 'input-price-self-defense', placeholder: '85' },
                { key: 'price_jiu_jitsu', label: 'Jiu-Jitsu', testid: 'input-price-jiu-jitsu', placeholder: '95' },
              ].map(({ key, label, testid, placeholder }) => {
                const raw = form[key];
                const num = Number(raw);
                const belowMin = raw !== '' && raw !== null && raw !== undefined && Number.isFinite(num) && num > 0 && num < 40;
                return (
                  <div className="grid gap-1.5" key={key}>
                    <span className="text-xs text-zinc-500">{label}</span>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                      <Input
                        type="number"
                        min="40"
                        step="1"
                        value={raw}
                        onChange={(e) => setField(key, e.target.value)}
                        placeholder={placeholder}
                        className={`bg-[#09090B] text-white pl-7 ${belowMin ? 'border-red-500' : 'border-zinc-800'}`}
                        data-testid={testid}
                        aria-invalid={belowMin || undefined}
                      />
                    </div>
                    {belowMin && (
                      <p className="text-xs text-red-400" data-testid={`${testid}-error`}>
                        Minimum session rate is $40.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-zinc-500">Set a price for each service you offer. Minimum $40 per session.</p>
          </div>

          {/* Service area */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="grid gap-2 sm:col-span-2">
              <Label htmlFor="service_area" className="text-zinc-300">City / service area</Label>
              <Input
                id="service_area"
                value={form.service_area}
                onChange={(e) => setField('service_area', e.target.value)}
                placeholder="e.g. Brooklyn"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="input-service-area"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="state" className="text-zinc-300">State</Label>
              <Input
                id="state"
                value={form.state}
                onChange={(e) => setField('state', e.target.value)}
                placeholder="e.g. NY"
                maxLength={20}
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="input-state"
              />
            </div>
            <p className="sm:col-span-3 text-xs text-zinc-500 -mt-1">
              Used to place you on the map for nearby clients. Approximate, we never publish your exact address.
            </p>
          </div>

          {/* Weekly schedule */}
          <div className="grid gap-2">
            <Label className="text-zinc-300">Weekly availability</Label>
            <WeeklyScheduleEditor
              value={form.weekly_schedule}
              onChange={(ws) => { setField('weekly_schedule', ws); refreshUser?.(); }}
            />
          </div>

          {/* Session location options */}
          <div className="grid gap-3 border border-zinc-800 rounded-md p-4 bg-[#0d0d10]">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="accepts_at_my_location" className="text-zinc-200 font-semibold">
                  Offer training at my address
                </Label>
                <p className="text-xs text-zinc-500 mt-1">
                  When enabled, clients can choose to come to you instead of you traveling to them.
                </p>
              </div>
              <Switch
                id="accepts_at_my_location"
                checked={!!form.accepts_at_my_location}
                onCheckedChange={(c) => setField('accepts_at_my_location', !!c)}
                data-testid="toggle-accepts-at-my-location"
              />
            </div>
            {form.accepts_at_my_location && (
              <Input
                value={form.trainer_address}
                onChange={(e) => setField('trainer_address', e.target.value)}
                placeholder="e.g. 123 Gym Lane, Brooklyn, NY"
                className="bg-[#09090B] border-zinc-700 text-white"
                data-testid="input-trainer-address"
              />
            )}
          </div>

          {/* Contact */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="input-phone"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_email" className="text-zinc-300">Contact email</Label>
              <Input
                id="contact_email"
                type="email"
                value={form.contact_email}
                onChange={(e) => setField('contact_email', e.target.value)}
                placeholder="you@example.com"
                className="bg-[#09090B] border-zinc-800 text-white"
                data-testid="input-contact-email"
              />
            </div>
          </div>

          {/* Portfolio: up to 4 photos + 1 video URL */}
          <div className="grid gap-3 border border-zinc-800 rounded-md p-4 bg-[#0d0d10]" data-testid="portfolio-section">
            <div>
              <Label className="text-zinc-200 font-semibold">Portfolio</Label>
              <p className="text-xs text-zinc-500 mt-1">
                Up to 4 action photos and 1 highlight video. Clients see these front and center.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="portfolio-photo-grid">
              {[0, 1, 2, 3].map((idx) => {
                const photo = form.portfolio_photos?.[idx];
                return (
                  <div
                    key={idx}
                    className="relative aspect-square rounded-md border border-dashed border-zinc-700 overflow-hidden bg-[#09090B] flex items-center justify-center"
                    data-testid={`portfolio-slot-${idx}`}
                  >
                    {photo ? (
                      <>
                        <img src={photo} alt={`Portfolio ${idx + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setField('portfolio_photos', (form.portfolio_photos || []).filter((_, i) => i !== idx))}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500/80 text-white flex items-center justify-center"
                          aria-label="Remove photo"
                          data-testid={`portfolio-remove-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <label className="w-full h-full flex flex-col items-center justify-center text-zinc-500 text-xs cursor-pointer hover:text-[#C0A062] transition-colors">
                        <Camera className="w-5 h-5 mb-1" />
                        Add photo
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (file.size > 10 * 1024 * 1024) {
                              toast.error('Photo is too large. Please pick one under 10MB.');
                              e.target.value = '';
                              return;
                            }
                            const compressing = toast.loading('Compressing photo…');
                            try {
                              const dataUrl = await compressImageFile(file);
                              const next = [...(form.portfolio_photos || [])];
                              next[idx] = dataUrl;
                              setField(
                                'portfolio_photos',
                                next.filter(Boolean).slice(0, 4)
                              );
                              toast.success('Photo added', { id: compressing });
                            } catch (err) {
                              toast.error('Could not process the image. Try a different photo.', { id: compressing });
                            } finally {
                              e.target.value = '';
                            }
                          }}
                          data-testid={`portfolio-input-${idx}`}
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="portfolio-video" className="text-xs text-zinc-400">Highlight video URL (YouTube or Vimeo)</Label>
              <Input
                id="portfolio-video"
                value={form.portfolio_video_url}
                onChange={(e) => setField('portfolio_video_url', e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                className="bg-[#09090B] border-zinc-700 text-white"
                data-testid="input-portfolio-video"
              />
            </div>
          </div>

          {/* Social links */}
          <div className="grid gap-3">
            <Label className="text-zinc-300">Social links (optional)</Label>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_instagram}
                  onChange={(e) => setField('social_instagram', e.target.value)}
                  placeholder="Instagram URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="input-instagram"
                />
              </div>
              <div className="relative">
                <Music2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_tiktok}
                  onChange={(e) => setField('social_tiktok', e.target.value)}
                  placeholder="TikTok URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="input-tiktok"
                />
              </div>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_youtube}
                  onChange={(e) => setField('social_youtube', e.target.value)}
                  placeholder="YouTube URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="input-youtube"
                />
              </div>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  value={form.social_facebook}
                  onChange={(e) => setField('social_facebook', e.target.value)}
                  placeholder="Facebook URL"
                  className="bg-[#09090B] border-zinc-800 text-white pl-9"
                  data-testid="input-facebook"
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#C0A062] text-black hover:bg-[#a98a52]"
            data-testid="save-profile-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
      <ImageCropper
        open={cropperOpen}
        onOpenChange={setCropperOpen}
        onCropped={(dataUrl) => setField('profile_image', dataUrl)}
        title="Crop your profile photo"
      />
    </Dialog>
  );
};

export default EditTrainerProfileDialog;
